import {
  CloseOutlined,
  LeftCircleOutlined,
  LoadingOutlined,
  RightCircleOutlined,
} from "@ant-design/icons";
import { Button, Form, Input, Select, Spin, Typography } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DeleteOrderDraft,
  PDetailsPrice,
  PlaceOrderRequest,
} from "../../../redux/actions/adminActions/newOrderRequestAction";
import { AllProductsAPI } from "../../../redux/actions/adminActions/allProductsAction";
import Alertify from "../../../services/alertify";
import { useLocation } from "wouter";
import { resetAdminDealers } from "../../../redux/reducers/adminReducers/adminDealersSlice";
import { getQueryParams } from "../../../utilities/getQueryParams";
import FullLoader from "../../../utilities/fullLoader";
import Decimal from "decimal.js";

const { Title } = Typography;

// ProductAndPaymentDuplicate
// This component renders the product selection and payment details UI
// for creating or editing an admin order request. It manages form state,
// fetches products (with pagination and search), validates pricing rules,
// and submits or saves drafts of the order.
//
// Note: setState calls are used throughout to keep local UI state in sync
// with form values and API results (see inline comments near each setState).
const ProductAndPaymentDuplicate = (props) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [, setProducts] = useState([{ pad: 0 }]);
  // `isChange` toggles when product/form values change and triggers
  // recalculation of totals in the `useEffect` below.
  const [isChange, setIsChange] = useState(false);
  // `setLocation` is used to navigate after placing an order or on errors.
  const [, setLocation] = useLocation();
  // Validation related state:
  const [errorMessages, setErrorMessages] = useState({}); // discount validation errors per product
  const [quantityErrorMessage, setQuantityErrorMessage] = useState({}); // quantity errors per product
  const [gtError, setGtError] = useState(); // grand total vs credit limit error
  const [fPercent, setFPercent] = useState(); // freight percent validation error
  // `freightType` stores the selected freight handling option.
  const [freightType, setFreightType] = useState("");
  // `disabledSubmit` toggles whether submit button is disabled due to validation
  const [disabledSubmit, setDisabledSubmit] = useState(false);

  // `displayedProducts` holds the current product list shown in the Select dropdown
  const [displayedProducts, setDisplayedProducts] = useState([]);
  // Pagination and loading state for product list
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0); // total available products from API
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false); // loading spinner for form submit

  const dropdownRef = useRef(null);
  const ITEMS_PER_PAGE = 20;

  const allProducts = useSelector((state) => state.all_product);

  // const all_products_options = useMemo(() => {
  //   return allProducts.all_products_slice || [];
  // }, [allProducts]);

  // Memoized list of products to show in dropdown.
  // It includes current displayedProducts plus any products already
  // in the form (to avoid losing entries when paginating/searching).
  const combinedProducts = useMemo(() => {
    const products = Array.isArray(displayedProducts)
      ? [...displayedProducts]
      : [];
    const productCodes = new Set(products.map((p) => p.erp_item_code));

    if (Array.isArray(props.formData?.products)) {
      props.formData.products.forEach((p) => {
        if (p.alp_code && !productCodes.has(p.alp_code)) {
          products.push({
            erp_item_code: p.alp_code,
            name: p.title,
            is_expired: 0, // Default to not expired
          });
          productCodes.add(p.alp_code);
        }
      });
    }
    return products;
  }, [displayedProducts, props.formData]);
  // console.log("props.formData", props.formData);
  // Fetch first page of products when dealer_code changes.
  // This effect resets displayedProducts and pagination state.
  useEffect(() => {
    if (props.formData.dealer_code) {
      setDisplayedProducts([]);
      setCurrentPage(1);
      setHasMore(true);

      dispatch(
        AllProductsAPI({
          dealer_code: props.formData.dealer_code,
          page: 1,
          per_page: ITEMS_PER_PAGE,
          responsibility_center: props.formData.branch_code,
        }),
      )
        .then((response) => {
          console.log(
            "response AllProductsAPI ============",
            response.payload.data.meta,
          );
          const products = response?.payload?.data.data || [];
          const total = response?.payload?.data?.meta?.total || 0;

          setDisplayedProducts(products); // update dropdown items
          setTotalProducts(total); // total products available
          setHasMore(products.length < total); // whether more pages exist
        })
        .catch((error) => {
          console.error("Error fetching the products:", error);
          setHasMore(false);
        });
    }
  }, [dispatch, props.formData.dealer_code]);

  // handleSearchChange
  // Triggered when the product Select's search input changes.
  // - updates `searchQuery` to store the user input
  // - resets displayed products and pagination
  // - fetches the first page of products using the search term
  const handleSearchChange = (value) => {
    setSearchQuery(value); // store current search text

    if (props.formData.dealer_code) {
      setDisplayedProducts([]);
      setCurrentPage(1);
      setHasMore(true);
      setIsLoadingMore(true);

      dispatch(
        AllProductsAPI({
          dealer_code: props.formData.dealer_code,
          page: 1,
          per_page: ITEMS_PER_PAGE,
          search: value, // Send search query
          responsibility_center: props.formData.branch_code,
        }),
      )
        .then((response) => {
          const products = response?.payload?.data?.data || [];
          const total = response?.payload?.data?.meta?.total || 0;

          setDisplayedProducts(products); // replace dropdown items with search results
          setTotalProducts(total); // update known total for pagination
          setHasMore(products.length < total); // whether more pages exist
          setIsLoadingMore(false); // stop the "loading more" spinner
        })
        .catch((error) => {
          console.error("Error fetching products:", error);
          setHasMore(false);
          setIsLoadingMore(false);
        });
    }
  };

  // loadMoreProducts
  // Called when the product dropdown is scrolled to the bottom to fetch the next page.
  // - sets `isLoadingMore` while a request is in-flight
  // - appends new items to `displayedProducts` and advances `currentPage`
  const loadMoreProducts = () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true); // show loading indicator for pagination
    const nextPage = currentPage + 1;

    dispatch(
      AllProductsAPI({
        dealer_code: props.formData.dealer_code,
        page: nextPage,
        per_page: ITEMS_PER_PAGE,
        search: searchQuery, // Include search in pagination
        responsibility_center: props.formData.branch_code,
      }),
    )
      .then((response) => {
        const newProducts = response?.payload?.data?.data || [];
        const total = response?.payload?.data?.meta?.total || 0;

        if (newProducts.length > 0) {
          // append newly fetched products to existing list
          setDisplayedProducts((prev) => {
            const updated = [...prev, ...newProducts];
            const totalLoaded = updated.length;
            setHasMore(totalLoaded < total); // update hasMore based on loaded count
            return updated;
          });

          setCurrentPage(nextPage); // advance page counter
          setTotalProducts(total); // update total if changed
        } else {
          setHasMore(false); // no more pages
        }

        setIsLoadingMore(false); // stop pagination spinner
      })
      .catch((error) => {
        console.error("Error loading more products:", error);
        setHasMore(false);
        setIsLoadingMore(false);
      });
  };

  // handleDropdownVisibleChange
  // When the product Select dropdown opens, ensure initial product data is loaded
  const handleDropdownVisibleChange = (open) => {
    if (open && displayedProducts.length === 0) {
      // Load initial products if not already loaded
      if (props.formData.dealer_code) {
        dispatch(
          AllProductsAPI({
            dealer_code: props.formData.dealer_code,
            page: 1,
            per_page: ITEMS_PER_PAGE,
            responsibility_center: props.formData.branch_code,
          }),
        )
          .then((response) => {
            // Fixed: API returns data directly, not data.data
            const products = response?.payload?.data?.data || [];
            // Fixed: Total is in meta.total, not data.total
            const total = response?.payload?.meta?.total || 0;

            setDisplayedProducts(products); // populate dropdown items
            setCurrentPage(1); // reset page to first
            setTotalProducts(total); // record total products
            setHasMore(products.length < total); // whether more pages available
          })
          .catch((error) => {
            console.error("Error fetching products:", error);
          });
      }
    }
  };
  // handlePopupScroll
  // Called when the product Select dropdown's internal scroll moves.
  // If the user scrolled to the bottom, trigger `loadMoreProducts()` to fetch next page.
  const handlePopupScroll = (e) => {
    const { target } = e;
    const threshold = 10;

    if (
      target.scrollTop + target.offsetHeight >=
      target.scrollHeight - threshold
    ) {
      loadMoreProducts();
    }
  };

  // handleSubmit
  // Validates the form, checks product-level and order-level business rules,
  // then dispatches the PlaceOrderRequest API. It also handles removing
  // any associated draft after successful submission.
  // State updates:
  // - `setDisabledSubmit(true)`: prevent double submissions while processing
  // - `setIsLoading(true)`: show global loading spinner during submit
  const handleSubmit = () => {
    setDisabledSubmit(true); // disable submit button while validation/submission runs
    setIsLoading(true); // show loader overlay

    const formValues = form.getFieldsValue();
    const products = formValues.products || [];
    // Convert relevant fields to numbers
    const convertedProducts = products.map((product) => ({
      ...product,
      packaging_quantity_wholesaler_box: Number(
        product.packaging_quantity_wholesaler_box,
      ),
      unit_price: Number(product.unit_price),
      msp: Number(product.msp),
      boxPrice: Number(product.boxPrice),
      quantity: Number(product.quantity),
      total_price: Number(product.total_price),
      discount: Number(product.discount),
      pad: Number(product.pad),
      gst: Number(product.gst),
      gstNumber: Number(product.gstNumber),
      final_price: Number(product.final_price),
    }));

    const subTotal = Number(formValues.subTotal);
    const totalGST = Number(formValues.totalGST);
    const grandTotal = Number(formValues.grandTotal);
    const params = getQueryParams();
    const uuIds = params.UUID;
    // const freight_percent = Number(formValues.freight_percent);
    // Update form values with converted numbers
    const freight_percent = isNaN(Number(formValues.freight_percent))
      ? 0
      : Number(formValues.freight_percent);
    form.setFieldsValue({
      products: convertedProducts,
      subTotal,
      totalGST,
      grandTotal,
      freight_percent,
    });

    const newErrorMessages = {};
    const quantityError = {};
    let hasError = false;

    convertedProducts.forEach((product, index) => {
      const min_price =
        product.packaging_quantity_wholesaler_box *
        product.msp *
        product.quantity;
      const pad = product.pad;
      const EPSILON = 1e-6;
      if (pad < min_price - EPSILON) {
        console.log("pad", pad);
        console.log("min_price", min_price - EPSILON);
        newErrorMessages[index] =
          `Price after discount is less than the minimum price.`;
        hasError = true;
      } else if (product.quantity < 1) {
        quantityError[index] = `Qunatity should be atleast 1.`;
        setDisabledSubmit(true); // keep submit disabled due to invalid quantity
        hasError = true;
      } else {
        const grand_Total = Number(formValues.grandTotal);
        const balanced_credit_limit = Number(formValues.balance_credit);

        if (
          props.formData.orderType !== "Blanket Order" &&
          grand_Total > balanced_credit_limit
        ) {
          var grandTotalError = `Grand Total should be less than or Equal to the Balanced Credit Limit.`;
          hasError = true;
          setGtError(grandTotalError); // show grand total vs credit limit error
          setDisabledSubmit(true); // disable submit while invalid
          return;
        }
      }
    });

    setErrorMessages(newErrorMessages); // show any product discount errors
    setQuantityErrorMessage(quantityError); // show quantity errors per product

    if (hasError) {
      setIsLoading(false); // stop loader if validation failed
      return;
    }
    const freight_percent_value = Number(form.getFieldValue("freight_percent"));
    if (freight_percent_value > 100) {
      setFPercent(
        "The Freight Percentage should be less than or equal to 100.",
      );
      setDisabledSubmit(true); // invalid freight percent, keep submit disabled
      setIsLoading(false); // stop loader
      return;
    }
    form
      .validateFields()
      .then((values) => {
        // console.log("Form Values:", updatedFormData);
        const updatedFormData = {
          ...props.formData,
          ...values,
          requestId: props.requestId,
        };
        if (freightType === "Freight As per Customer PO") {
          updatedFormData.subsidy = null;
        }
        if (freightType === "Freight Added to Invoice") {
          updatedFormData.freight_percent = null;
        }
        dispatch(PlaceOrderRequest(updatedFormData))
          .then((response) => {
            const order_sent_status =
              response?.payload?.data?.erp_response.responses[0]?.status;
            // console.log("Response is ++++++++++++++++:", updatedFormData);
            props.setFormData({});
            dispatch(resetAdminDealers());
            // console.log("props.formData-----------------", props.formData);
            if (order_sent_status === 400) {
              Alertify.error("Order failed!");
              setLocation("/admin/failed-orders");
            } else {
              console.log(response);
              if (response?.error?.message) {
                Alertify.error(response?.error?.message);
                setLocation("/admin/orders");
                return;
              }
              Alertify.success("Order placed successfully!");
              setLocation("/admin/orders");
            }
            // add draft order delete API
            if (uuIds) {
              const draftDeleteReqData = {
                uuid: uuIds,
              };
              dispatch(DeleteOrderDraft(draftDeleteReqData));
            } else if (props.uuid) {
              const draftDeleteReqData = {
                uuid: props.uuid,
              };
              dispatch(DeleteOrderDraft(draftDeleteReqData));
            }
            setIsLoading(false);
            props.onNext(values);
          })
          .catch((error) => {
            console.error("Error Place Order Request:", error);
            setIsLoading(false);
          });
      })
      .catch((errorInfo) => {
        console.error("Validation Failed:", errorInfo);
        Alertify.error("Please fill in all required fields.");
        setIsLoading(false);
      });
  };

  // handleDraft
  // Validates the form and moves to the next step while preserving current
  // form values as a draft. After saving draft, navigates back in history.
  const handleDraft = () => {
    form
      .validateFields()
      .then((values) => {
        // console.log("Form Values:", values);
        props.onNext(values); // propagate values to parent for draft saving
        window.history.back(); // return to previous page
      })
      .catch((errorInfo) => {
        console.error("Validation Failed:", errorInfo);
        Alertify.error("Please fill in all required fields.");
      });
  };

  // searchProduct
  // Given an ERP item code (value) and the product field index (fieldName),
  // fetches product pricing/details from the API and populates the form row.
  // After updating the form fields we set `isChange` so totals recalc.
  const searchProduct = (value, fieldName) => {
    // console.log("Formadata is:", props.formData);
    const data = {
      erp_item_code: value,
      dealer_code: props.formData.dealer_code,
      responsibility_center: props.formData.branch_code,
    };
    dispatch(PDetailsPrice(data))
      .then((response) => {
        const productDetails = response.payload?.data?.data;
        console.log("The response is:", response);
        if (!response?.payload) {
          console.log("Product not found!!!!");

          const currentSections = form.getFieldValue("products") || [];

          const updatedSections = currentSections.map((section, index) =>
            index === fieldName
              ? {
                  pad: 0,
                  alp_code: undefined,
                  title: undefined,
                }
              : section,
          );

          form.setFieldsValue({
            products: updatedSections,
          });

          setProducts(updatedSections);
          setIsChange(true);
          return;
        }
        const box_price = parseFloat(
          parseFloat(productDetails.pricing.unit_price) *
            parseFloat(
              productDetails.product.packaging_quantity_wholesaler_box,
            ),
        );
        const gst_amount =
          (parseFloat(productDetails.product.gst) * box_price) / 100;
        const price_after_gst = parseFloat(box_price) + parseFloat(gst_amount);
        const currentSections = form.getFieldValue("products");
        const updatedSections = currentSections.map((section, index) =>
          index === fieldName
            ? {
                ...section,
                alp_code: productDetails.product.erp_item_code,
                title:
                  productDetails.product.title +
                  "-" +
                  productDetails.product.erp_item_code,
                packaging_quantity_wholesaler_box:
                  productDetails.product.packaging_quantity_wholesaler_box,
                unit_price: productDetails.pricing.unit_price,
                boxPrice: box_price.toFixed(5),
                quantity: 1,
                discount: 0,
                total_price: box_price.toFixed(5),
                pad: box_price.toFixed(5),
                gst: productDetails.product.gst,
                gstNumber: (productDetails.product.gst * box_price) / 100,
                final_price: price_after_gst.toFixed(5),
                msp: productDetails.pricing.msp_price,
              }
            : section,
        );

        form.setFieldsValue({
          products: updatedSections,
        });
        setProducts(updatedSections); // update local copy used for calculations
        setIsChange(true); // trigger totals recalculation effect
      })
      .catch((error) => {
        console.error("Error fetching product details:", error);
      });
  };

  // useEffect(() => {
  //   dispatch(AllProductsAPI({ dealer_code: props.formData.dealer_code }))
  //     .then((response) => {
  //       // Handle response if needed
  //     })
  //     .catch((error) => {
  //       console.error("Error fetching the products;", error);
  //     });
  // }, [dispatch]);

  useEffect(() => {
    // Initialize form-level fields coming from props.formData
    form.setFieldsValue({
      payment_method: props.formData.transaction_type,
      balance_credit: props.formData.balance_credit,
      freight_type: props.formData.freight_type,
      freight_percent: props.formData.freight_percent,
      subsidy: props.formData.subsidy,
      // boxPrice: box_price,
    });
    // Set local freightType state to control conditional fields in UI
    setFreightType(props.formData.freight_type);
    // setInitialFreightPercent
  }, [form, props.formData]);
  const [subTotalValue, setSubTotalValue] = useState({
    subTotal: "",
    grandTotal: "",
    totalGST: "",
  });
  // Autofill data from props.formData into the form fields
  useEffect(() => {
    if (
      Array.isArray(props.formData.products) &&
      props.formData.products.length > 0
    ) {
      form.setFieldsValue({
        products: props.formData.products?.map((product) => ({
          boxPrice: parseFloat(
            parseFloat(product.unit_price) *
              parseFloat(product.packaging_quantity_wholesaler_box),
          ).toFixed(5),
          alp_code: product.alp_code,
          title: product.title,
          packaging_quantity_wholesaler_box:
            product.packaging_quantity_wholesaler_box,
          gst: product.gst,
          quantity: product.quantity,
          unit_price: parseFloat(product.unit_price).toFixed(5),
          final_price: parseFloat(
            (product.gst *
              (parseFloat(product.unit_price) *
                parseFloat(product.packaging_quantity_wholesaler_box) *
                product.quantity -
                (product.discount *
                  (parseFloat(product.unit_price) *
                    parseFloat(product.packaging_quantity_wholesaler_box) *
                    product.quantity)) /
                  100)) /
              100 +
              (parseFloat(product.unit_price) *
                parseFloat(product.packaging_quantity_wholesaler_box) *
                product.quantity -
                (product.discount *
                  (parseFloat(product.unit_price) *
                    parseFloat(product.packaging_quantity_wholesaler_box) *
                    product.quantity)) /
                  100),
          ).toFixed(5),
          discount: product.discount,
          total_price: parseFloat(
            parseFloat(product.unit_price) *
              parseFloat(product.packaging_quantity_wholesaler_box) *
              product.quantity,
          ).toFixed(5),
          pad: parseFloat(
            parseFloat(product.unit_price) *
              parseFloat(product.packaging_quantity_wholesaler_box) *
              product.quantity -
              (product.discount *
                (parseFloat(product.unit_price) *
                  parseFloat(product.packaging_quantity_wholesaler_box) *
                  product.quantity)) /
                100,
          ).toFixed(5),
          gstNumber: parseFloat(
            (product.gst *
              (parseFloat(product.unit_price) *
                parseFloat(product.packaging_quantity_wholesaler_box) *
                product.quantity -
                (product.discount *
                  (parseFloat(product.unit_price) *
                    parseFloat(product.packaging_quantity_wholesaler_box) *
                    product.quantity)) /
                  100)) /
              100,
          ).toFixed(5),
          msp: product.msp,
        })),
      });
    } else {
      // Ensure at least one product entry is present so an item shows open by default
      form.setFieldsValue({ products: [{ pad: 0 }] });
    }

    const currentSections = form.getFieldValue("products") || [];
    setProducts(currentSections);
    const subTotal = currentSections.reduce(
      (acc, section) => acc + parseFloat(section.pad || 0),
      0,
    );
    const grandTotal = currentSections.reduce(
      (acc, section) => acc + parseFloat(section.final_price || 0),
      0,
    );
    const totalGST = currentSections.reduce(
      (acc, section) => acc + parseFloat(section.gstNumber || 0),
      0,
    );
    setSubTotalValue({
      subTotal: subTotal.toFixed(2),
      grandTotal: grandTotal.toFixed(2),
      totalGST: totalGST.toFixed(2),
    });
    const freight_percent_value = Number(form.getFieldValue("freight_percent"));
    const grand_Total = Number(grandTotal);
    const balanced_credit_limit = Number(form.getFieldValue("balance_credit"));

    if (
      props.formData.orderType !== "Blanket Order" &&
      grand_Total > balanced_credit_limit
    ) {
      var grandTotalError = `Grand Total should be less than or Equal to the Balanced Credit Limit.`;
      setGtError(grandTotalError);
      // console.log(
      //   `props.formData.balance_credit:::::::::${props.formData.balance_credit}`
      // );
      setDisabledSubmit(true);
    } else if (freight_percent_value > 100) {
      setFPercent(
        "The Freight Percentage should be less than or equal to 100.",
      );
      setDisabledSubmit(true);
    } else {
      setGtError(null);
      setDisabledSubmit(false);
      setFPercent(null);
    }
  }, [form, props.formData]);

  const quantityChange = (value, fieldName) => {
    // quantityChange
    // Updates quantity-related fields for a product row when quantity changes:
    // - recalculates `total_price`, `pad`, `gstNumber`, and `final_price`
    // - calls `form.setFieldsValue` to persist changes in the antd form
    // We don't update `setProducts` here directly; `onValuesChange` or
    // `isChange` flow will pick up the updated form values to recalc totals.
    const currentSections = form.getFieldValue("products");
    const currentSection = currentSections[fieldName];
    if (value < 1) {
      Alertify.error("Minimum quantity should be 1.");
      return;
    }
    if (currentSection) {
      const boxPrice = currentSection.boxPrice || 0;
      const quantity = parseFloat(value) || 0;
      const total_price = parseFloat(boxPrice * quantity).toFixed(5);
      const discount = currentSection.discount;
      const totalDiscount = (discount * total_price) / 100;
      const pad = parseFloat((total_price - totalDiscount).toFixed(5));
      const gst = parseFloat(currentSection.gst);
      const gstNumber = parseFloat((gst * pad) / 100);
      const final_price = parseFloat((gst * pad) / 100 + pad).toFixed(5);
      form.setFieldsValue({
        products: currentSections.map((section, index) =>
          index === fieldName
            ? { ...section, quantity, total_price, pad, final_price, gstNumber }
            : section,
        ),
      });
    }
  };

  // const discountChange = (value, fieldName) => {
  //   if (value >= 100) {
  //     Alertify.error("Discount should be less than 100%.");
  //     return;
  //   }
  //   const currentSections = form.getFieldValue("products");
  //   const currentSection = currentSections[fieldName];
  //   if (currentSection) {
  //     const discount = value;
  //     const total_price =
  //       parseFloat(currentSection.total_price).toFixed(5) || 0;
  //     const min_price =
  //       parseFloat(currentSection.packaging_quantity_wholesaler_box) *
  //       parseFloat(currentSection.msp) *
  //       parseFloat(currentSection.quantity);
  //     var pad = parseFloat(total_price - (discount * total_price) / 100);
  //     console.log("min_price value is:", min_price);
  //     console.log("pad value is:", pad);

  //     const gst = parseFloat(currentSection.gst);
  //     const gstNumber = parseFloat((gst * pad) / 100).toFixed(5);

  //     const final_price = parseFloat((gst * pad) / 100 + pad).toFixed(5);
  //     if (pad < min_price) {
  //       const max_discount = parseFloat(100 - (min_price / total_price) * 100);
  //       Alertify.error("This discount is not allowed on this product.");
  //       pad = 0;
  //       form.setFieldsValue({
  //         products: currentSections.map((section, index) =>
  //           index === fieldName ? { ...section, pad } : section,
  //         ),
  //       });
  //       return;
  //     }
  //     form.setFieldsValue({
  //       products: currentSections.map((section, index) =>
  //         index === fieldName
  //           ? { ...section, discount, pad, final_price, gstNumber }
  //           : section,
  //       ),
  //     });
  //   }
  // };

  const discountChange = (value, fieldName) => {
    // discountChange
    // Calculates the price after discount (`pad`) using Decimal for accuracy,
    // enforces the minimum allowed price (based on MSP and packaging quantity),
    // and updates `pad`, `final_price`, and `gstNumber` on the form row.
    // If discount is invalid, resets `pad` to 0 and shows an error.
    const discount = new Decimal(value || 0);

    if (discount.greaterThanOrEqualTo(100)) {
      Alertify.error("Discount should be less than 100%.");
      return;
    }

    const currentSections = form.getFieldValue("products");
    const currentSection = currentSections?.[fieldName];

    if (!currentSection) {
      return;
    }

    // Convert all incoming values to Decimal
    const totalPrice = new Decimal(currentSection.total_price || 0);

    const packagingQuantity = new Decimal(
      currentSection.packaging_quantity_wholesaler_box || 0,
    );

    const msp = new Decimal(currentSection.msp || 0);

    const quantity = new Decimal(currentSection.quantity || 0);

    const gst = new Decimal(currentSection.gst || 0);

    // Minimum allowed price
    const minPrice = packagingQuantity.mul(msp).mul(quantity);

    // Price after discount
    const pad = totalPrice.minus(discount.mul(totalPrice).div(100));

    console.log("min_price value is:", minPrice.toFixed(5));
    console.log("pad value is:", pad.toFixed(5));

    // Check minimum price
    if (pad.lessThan(minPrice)) {
      const maxDiscount = new Decimal(100).minus(
        minPrice.div(totalPrice).mul(100),
      );

      console.log("Maximum allowed discount:", maxDiscount.toFixed(5));

      Alertify.error("This discount is not allowed on this product.");

      const resetPad = "0.00000";

      form.setFieldsValue({
        products: currentSections.map((section, index) =>
          index === fieldName
            ? {
                ...section,
                pad: resetPad,
              }
            : section,
        ),
      });

      return;
    }

    // GST amount
    const gstNumber = gst.mul(pad).div(100).toFixed(5);

    // Final price including GST
    const finalPrice = gst.mul(pad).div(100).plus(pad).toFixed(5);

    form.setFieldsValue({
      products: currentSections.map((section, index) =>
        index === fieldName
          ? {
              ...section,
              discount: value,
              pad: pad.toFixed(5),
              final_price: finalPrice,
              gstNumber,
            }
          : section,
      ),
    });
  };
  useEffect(() => {
    if (isChange) {
      const currentSections = form.getFieldValue("products") || [];
      // When product rows change (`isChange`), sync local `products` state
      // used by some calculations and UI with the form data.
      setProducts(currentSections);
      const subTotal = currentSections.reduce(
        (acc, section) => acc + parseFloat(section.pad || 0),
        0,
      );
      const grandTotal = currentSections.reduce(
        (acc, section) => acc + parseFloat(section.final_price || 0),
        0,
      );
      const totalGST = currentSections.reduce(
        (acc, section) => acc + parseFloat(section.gstNumber || 0),
        0,
      );
      setSubTotalValue({
        subTotal: subTotal.toFixed(2),
        grandTotal: grandTotal.toFixed(2),
        totalGST: totalGST.toFixed(2),
      });
      const freight_percent_value = Number(
        form.getFieldValue("freight_percent"),
      );
      const grand_Total = Number(grandTotal);
      const balanced_credit_limit = Number(
        form.getFieldValue("balance_credit"),
      );
      if (
        props.formData.orderType !== "Blanket Order" &&
        grand_Total > balanced_credit_limit
      ) {
        var grandTotalError = `Grand Total should be less than or Equal to the Balanced Credit Limit.`;
        setGtError(grandTotalError);
        setDisabledSubmit(true);
      } else if (freight_percent_value > 100) {
        setFPercent(
          "The Freight Percentage should be less than or equal to 100.",
        );
        setDisabledSubmit(true);
      } else {
        setGtError(null);
        setDisabledSubmit(false);
        setFPercent(null);
      }
      // console.log("subtotalValue is:-------------------", subTotal);
      // console.log("Section is:-------------------", products);
      setIsChange(false);
    }
  }, [form, isChange]);

  useEffect(() => {
    form.setFieldsValue({
      subTotal: subTotalValue?.subTotal,
      grandTotal: subTotalValue?.grandTotal,
      totalGST: subTotalValue?.totalGST,
    });
    setIsChange(false);
  }, [form, subTotalValue]);

  const handleValueChange = () => {
    const currentSections = form.getFieldValue("products") || [];
    setProducts(currentSections);
    const subTotal = currentSections?.reduce(
      (acc, section) => acc + parseFloat(section.pad || 0),
      0,
    );
    const grandTotal = currentSections.reduce(
      (acc, section) => acc + parseFloat(section.final_price || 0),
      0,
    );
    const totalGST = currentSections.reduce(
      (acc, section) => acc + parseFloat(section.gstNumber || 0),
      0,
    );

    const newErrorMessages = {};
    const quantityError = {};
    let hasError = false;

    currentSections.forEach((product, index) => {
      const min_price =
        product.packaging_quantity_wholesaler_box *
        product.msp *
        product.quantity;
      // const pad = product.pad;
      const pad = parseFloat(
        (
          product.packaging_quantity_wholesaler_box *
            product.unit_price *
            product.quantity -
          (product.discount *
            product.packaging_quantity_wholesaler_box *
            product.unit_price *
            product.quantity) /
            100
        ).toFixed(2),
      );
      const freight_percent_value = Number(
        form.getFieldValue("freight_percent"),
      );
      const grand_Total = Number(grandTotal);
      const balanced_credit_limit = Number(
        form.getFieldValue("balance_credit"),
      );
      const EPSILON = 1e-6;
      if (pad < min_price - EPSILON) {
        newErrorMessages[index] =
          `Price after discount is less than the minimum price.`;
        hasError = true;
        setDisabledSubmit(true);
        return;
      } else if (product.quantity < 1) {
        quantityError[index] = `Qunatity should be atleast 1.`;
        setDisabledSubmit(true);
        hasError = true;
      } else if (
        props.formData.orderType !== "Blanket Order" &&
        grand_Total > balanced_credit_limit
      ) {
        var grandTotalError = `Grand Total should be less than or Equal to the Balanced Credit Limit.`;
        hasError = true;
        setGtError(grandTotalError);
        setDisabledSubmit(true);
      } else if (freight_percent_value > 100) {
        setFPercent(
          "The Freight Percentage should be less than or equal to 100.",
        );
        setDisabledSubmit(true);
      } else {
        setGtError(null);
        setDisabledSubmit(false);
        setFPercent(null);
      }
    });

    setErrorMessages(newErrorMessages);
    setQuantityErrorMessage(quantityError);

    if (!hasError) {
      setErrorMessages({});
      setQuantityErrorMessage({});
    }
    form.setFieldsValue({
      subTotal: subTotal.toFixed(5),
      grandTotal: grandTotal.toFixed(5),
      totalGST: totalGST.toFixed(5),
    });
  };
  const handlePrevious = () => {
    // handlePrevious
    // Prepare and normalize form values (convert strings to numbers/strings with fixed decimals)
    // then call `props.handlePrev(values)` to navigate to the previous step.
    // We set sanitized values back on the form before calling the parent handler.
    const formValues = form.getFieldsValue();
    const products = formValues.products || [];
    // Convert relevant fields to numbers
    const convertedProducts = products.map((product) => ({
      ...product,
      packaging_quantity_wholesaler_box: Number(
        product.packaging_quantity_wholesaler_box,
      ),
      unit_price: Number(product.unit_price).toFixed(5),
      msp: Number(product.msp),
      boxPrice: Number(product.boxPrice).toFixed(5),
      quantity: Number(product.quantity),
      total_price: Number(product.total_price).toFixed(5),
      discount: Number(product.discount),
      pad: Number(product.pad).toFixed(5),
      gst: Number(product.gst),
      gstNumber: Number(product.gstNumber),
      final_price: Number(product.final_price).toFixed(5),
    }));

    const subTotal = Number(formValues.subTotal);
    const totalGST = Number(formValues.totalGST);
    const grandTotal = Number(formValues.grandTotal);
    // const freight_percent = Number(formValues.freight_percent);

    // Update form values with converted numbers
    const freight_percent = isNaN(Number(formValues.freight_percent))
      ? 0
      : Number(formValues.freight_percent);
    form.setFieldsValue({
      products: convertedProducts,
      subTotal,
      totalGST,
      grandTotal,
      freight_percent,
    });

    const values = form.getFieldsValue();
    // console.log("Form Values:", values);
    props.handlePrev(values);
  };

  const handleFreightChange = (value) => {
    // handleFreightChange
    // Update local `freightType` state which controls conditional freight fields
    // shown in the Payment Details section.
    setFreightType(value);
  };

  return (
    <>
      <div className="border rounded-lg p-4 w-full relative">
        <Form
          form={form}
          layout="vertical"
          className="text-left"
          onFinish={handleSubmit}
          onValuesChange={handleValueChange}
          initialValues={{ remember: true, products: [{ pad: 0 }] }}
        >
          <div className="border p-4 rounded-lg">
            <Title level={3} className="text-left py-4">
              Product Details
            </Title>

            <Form.List name="products">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name: fieldName }) => (
                    <div
                      key={key}
                      className="add relative border p-4 rounded-lg mb-5 bg-slate-50"
                    >
                      {fields.length > 1 && (
                        <Button
                          type="text"
                          icon={<CloseOutlined />}
                          className="absolute right-1 top-1 h-[20px] w-[20px]"
                          onClick={() => remove(fieldName)}
                        />
                      )}
                      <div className="sm:flex">
                        <div className="w-full sm:me-4">
                          <Form.Item
                            label="Title"
                            name={[fieldName, "alp_code"]}
                            rules={[
                              {
                                required: true,
                                message: "Please select Product!",
                              },
                            ]}
                          >
                            <Select
                              showSearch
                              placeholder="Select Product"
                              onChange={(value) => {
                                searchProduct(value, fieldName);
                                setIsChange(true);
                              }}
                              onSearch={handleSearchChange}
                              onDropdownVisibleChange={
                                handleDropdownVisibleChange
                              }
                              onPopupScroll={handlePopupScroll}
                              filterOption={(input, option) =>
                                option?.name
                                  ?.toLowerCase()
                                  .includes(input.toLowerCase()) ||
                                option?.value
                                  ?.toLowerCase()
                                  .includes(input.toLowerCase())
                              }
                              dropdownRender={(menu) => (
                                <>
                                  {menu}
                                  {isLoadingMore && (
                                    <div
                                      style={{
                                        textAlign: "center",
                                        padding: "8px",
                                        borderTop: "1px solid #f0f0f0",
                                      }}
                                    >
                                      <Spin
                                        indicator={
                                          <LoadingOutlined
                                            style={{ fontSize: 24 }}
                                            spin
                                          />
                                        }
                                      />
                                    </div>
                                  )}
                                  {!hasMore && displayedProducts.length > 0 && (
                                    <div
                                      style={{
                                        textAlign: "center",
                                        padding: "8px",
                                        color: "#999",
                                        fontSize: "12px",
                                      }}
                                    >
                                      No more products
                                    </div>
                                  )}
                                </>
                              )}
                              options={combinedProducts.map((items) => ({
                                key: `${items.erp_item_code}`,
                                value: `${items.erp_item_code}`,
                                name: items.name,
                                label: (
                                  <div
                                    className={`flex justify-between p-2 ${
                                      items.is_expired === 1
                                        ? "bg-gray-100"
                                        : ""
                                    }`}
                                  >
                                    <div className="flex">
                                      {items.name},{" "}
                                      {items.erp_item_code?.toUpperCase()}
                                    </div>
                                    {items.is_expired === 1 && (
                                      <div
                                        className="ant-tag ant-tag-volcano css-dev-only-do-not-override-1oemflq m-0"
                                        style={{ color: "red" }}
                                      >
                                        Expired
                                      </div>
                                    )}
                                  </div>
                                ),
                                disabled: items.is_expired === 1,
                              }))}
                            />
                          </Form.Item>

                          <Form.Item
                            className="hidden"
                            label="Title"
                            name={[fieldName, "title"]}
                          >
                            <Input placeholder="title" disabled />
                          </Form.Item>
                        </div>
                        <div className="w-full sm:flex ">
                          <Form.Item
                            className="sm:me-4"
                            label="Pack Of"
                            name={[
                              fieldName,
                              "packaging_quantity_wholesaler_box",
                            ]}
                          >
                            <Input
                              placeholder="pack of"
                              type="number"
                              disabled
                            />
                          </Form.Item>
                          <Form.Item
                            className="sm:me-4"
                            label="Unit Price"
                            name={[fieldName, "unit_price"]}
                          >
                            <Input
                              placeholder="Unit Price"
                              type="number"
                              disabled
                            />
                          </Form.Item>
                          <Form.Item
                            className="hidden"
                            label="MSP"
                            name={[fieldName, "msp"]}
                          >
                            <Input placeholder="MSP" type="number" disabled />
                          </Form.Item>
                          <Form.Item
                            label="Box Price"
                            name={[fieldName, "boxPrice"]}
                          >
                            <Input
                              placeholder="Box Price"
                              type="number"
                              disabled
                            />
                          </Form.Item>
                        </div>
                      </div>
                      <div className="w-full sm:flex ">
                        <Form.Item
                          className="sm:me-4"
                          label="Quantity(No. of Boxes)"
                          name={[fieldName, "quantity"]}
                          validateStatus={
                            quantityErrorMessage[fieldName] ? "error" : ""
                          }
                          help={quantityErrorMessage[fieldName]}
                        >
                          <Input
                            placeholder="Number of Boxes"
                            type="number"
                            onChange={(e) => {
                              quantityChange(e.target.value, fieldName);
                              setIsChange(true);
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          className="sm:me-4"
                          label="Total Price"
                          name={[fieldName, "total_price"]}
                        >
                          <Input
                            placeholder="Total Price"
                            type="number"
                            disabled
                          />
                        </Form.Item>
                        <Form.Item
                          label="Discount"
                          name={[fieldName, "discount"]}
                          className="sm:me-4 sm:w-[16.67%]"
                          validateStatus={
                            errorMessages[fieldName] ? "error" : ""
                          }
                          help={errorMessages[fieldName]}
                        >
                          <Input
                            placeholder="Discount"
                            type="text"
                            step="0.00001"
                            onChange={(e) => {
                              let value = e.target.value;
                              // Allow any number of digits before decimal and up to 5 after
                              if (
                                value === "" ||
                                /^\d*\.?\d{0,5}$/.test(value)
                              ) {
                                discountChange(value, fieldName);
                                setIsChange(true);
                              }
                            }}
                            onKeyDown={(e) => {
                              const charStr = e.key;
                              const currentValue = e.target.value;
                              const cursorPosition = e.target.selectionStart;

                              // Allow navigation keys
                              if (
                                [
                                  "Backspace",
                                  "Delete",
                                  "ArrowLeft",
                                  "ArrowRight",
                                  "Tab",
                                ].includes(charStr)
                              ) {
                                return;
                              }

                              // Only allow digits and one decimal point
                              if (!/[\d.]/.test(charStr)) {
                                e.preventDefault();
                                return;
                              }

                              // Prevent multiple decimal points
                              if (
                                charStr === "." &&
                                currentValue.includes(".")
                              ) {
                                e.preventDefault();
                                return;
                              }

                              // Only limit decimal places if cursor is after decimal point
                              if (currentValue.includes(".")) {
                                const parts = currentValue.split(".");
                                const decimalPart = parts[1];
                                const decimalPosition = parts[0].length + 1;

                                // Only restrict if we're adding to the decimal part and it already has 5 digits
                                if (
                                  cursorPosition >= decimalPosition &&
                                  decimalPart.length >= 5
                                ) {
                                  e.preventDefault();
                                }
                              }
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          className="sm:me-4"
                          label="Price After Discount"
                          name={[fieldName, "pad"]}
                        >
                          <Input
                            placeholder="Price After Discount"
                            type="number"
                            disabled
                          />
                        </Form.Item>
                        <Form.Item
                          className="sm:me-4"
                          label="GST"
                          name={[fieldName, "gst"]}
                        >
                          <Input placeholder="GST" type="number" disabled />
                        </Form.Item>
                        <Form.Item
                          className="hidden"
                          label="GST Number"
                          name={[fieldName, "gstNumber"]}
                        >
                          <Input
                            placeholder="GST Number"
                            type="number"
                            disabled
                          />
                        </Form.Item>
                        <Form.Item
                          label="Price After GST"
                          name={[fieldName, "final_price"]}
                        >
                          <Input
                            placeholder="Price After GST"
                            type="number"
                            disabled
                          />
                        </Form.Item>
                      </div>
                    </div>
                  ))}
                  <Form.Item className="mt-5 flex justify-center">
                    <Button
                      className="Paragraph-white border"
                      onClick={() => add({ pad: 0 })}
                    >
                      Add Product
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>
          <div className="border p-4 rounded-lg mt-4">
            <Title level={3} className="text-left py-4">
              Payment Details
            </Title>
            <div className="sm:flex">
              <div className="w-full sm:me-4">
                <Form.Item
                  label="Freight Type"
                  name="freight_type"
                  rules={[
                    {
                      required: true,
                      message: "Please select Freight Type",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Select Freight Type"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    onChange={(value) => handleFreightChange(value)}
                    options={[
                      {
                        value: "Freight to Pay by Customer",
                        label: "Freight to Pay by Customer",
                      },
                      {
                        value: "Freight As per Customer PO",
                        label: "Freight As per Customer PO",
                      },
                      {
                        value: "Freight Added to Invoice",
                        label: "Freight Added to Invoice",
                      },
                    ]}
                  />
                </Form.Item>
              </div>
              <div className="w-full sm:me-4">
                {freightType === "Freight As per Customer PO" && (
                  <Form.Item
                    className="sm:me-4"
                    label="Freight Percent"
                    name="freight_percent"
                    validateStatus={fPercent ? "error" : ""}
                    help={fPercent}
                    rules={[
                      {
                        required: true,
                        message: "Please type Freight Percent",
                      },
                    ]}
                  >
                    <Input placeholder="Freight Percent" type="number" />
                  </Form.Item>
                )}
                {freightType === "Freight Added to Invoice" && (
                  <Form.Item
                    className="sm:me-4"
                    label="Freight Subsidy"
                    name="subsidy"
                  >
                    <Select
                      showSearch
                      placeholder="Select Freight Subsidy"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={[
                        { value: "", label: "0%" },
                        { value: "10%", label: "10%" },
                        { value: "20%", label: "20%" },
                        { value: "30%", label: "30%" },
                        { value: "40%", label: "40%" },
                        { value: "50%", label: "50%" },
                        { value: "100%", label: "100%" },
                      ]}
                    />
                  </Form.Item>
                )}
              </div>
              <div className="w-full sm:me-4"></div>
            </div>
            <div className="w-full sm:flex">
              <div className="w-full">
                <Form.Item
                  className="sm:me-4"
                  label="Total Weight"
                  name="totalWeight"
                >
                  <Input placeholder="Total Weight" type="number" disabled />
                </Form.Item>
              </div>
              <div className="w-full">
                <Form.Item
                  className="sm:me-4"
                  label="Sub Total"
                  name="subTotal"
                >
                  <Input placeholder="Sub Total" type="number" disabled />
                </Form.Item>
              </div>
              <div className="w-full">
                <Form.Item label="Total GST" name="totalGST">
                  <Input placeholder="Total GST" type="number" disabled />
                </Form.Item>
              </div>
            </div>
            <div className="w-full sm:flex">
              <div className="w-full">
                <Form.Item
                  className="sm:me-4"
                  label="Grand Total"
                  name="grandTotal"
                  validateStatus={gtError ? "error" : ""}
                  help={gtError}
                >
                  <Input placeholder="Grand Total" type="number" disabled />
                </Form.Item>
              </div>
              <div className="w-full">
                <Form.Item
                  className="sm:me-4"
                  label="Payment Method"
                  name="payment_method"
                >
                  <Input placeholder="Payment Method" disabled />
                </Form.Item>
              </div>
              <div className="w-full mx-2">
                <Form.Item label="Balanced Credit Limit" name="balance_credit">
                  <Input placeholder="Balanced Credit Limit" disabled />
                </Form.Item>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center w-full mt-4">
            <Button
              className="text-md rounded-lg"
              onClick={handlePrevious}
              disabled={props.currentStep <= 0}
            >
              <LeftCircleOutlined className="mb-[-3px]" /> PREVIOUS
            </Button>
            <div>
              <Button
                className="text-md rounded-lg mr-4"
                type="primary"
                htmlType="submit"
                onClick={handleDraft}
              >
                SAVE DRAFT
              </Button>
              <Button
                className="text-md rounded-lg"
                type="primary"
                // htmlType="submit"
                disabled={disabledSubmit}
                onClick={handleSubmit}
              >
                Submit <RightCircleOutlined className="mb-[-3px]" />
              </Button>
            </div>
          </div>
        </Form>
        <FullLoader isLoading={isLoading} />
      </div>
    </>
  );
};

export default ProductAndPaymentDuplicate;
